// src/pages/finance-portal/reports/ProjectProfitability.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Stack,
} from '@mui/material';
import EngineeringIcon from '@mui/icons-material/Engineering';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, formatPercent,
    firstOfYearIso, todayIso, downloadCsv,
} from './reportShared';

export default function ProjectProfitability() {
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/project-profitability', {
                params: { start_date: start, end_date: end },
            });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load project profitability');
        } finally { setLoading(false); }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <EngineeringIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Project Profitability</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <TextField label="Start" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Grouped by journal-entry reference. {(data.rows || []).length} projects.
                        </Typography>
                        <Button size="small" startIcon={<DownloadIcon />}
                            onClick={() => downloadCsv(`project_profitability_${start}_${end}.csv`, data.rows || [])}>
                            CSV
                        </Button>
                    </Stack>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project</TableCell>
                                    <TableCell align="right">Revenue</TableCell>
                                    <TableCell align="right">Direct Cost</TableCell>
                                    <TableCell align="right">Margin</TableCell>
                                    <TableCell align="right">Margin %</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.rows || []).map((r) => (
                                    <TableRow key={r.project_code}>
                                        <TableCell>{r.project_name}</TableCell>
                                        <TableCell align="right">{formatCurrency(r.revenue)}</TableCell>
                                        <TableCell align="right">{formatCurrency(r.direct_cost)}</TableCell>
                                        <TableCell align="right"
                                            sx={{ color: r.margin >= 0 ? ACCENT : '#ef4444' }}>
                                            {formatCurrency(r.margin)}
                                        </TableCell>
                                        <TableCell align="right">{formatPercent(r.margin_percent)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ '& td': { borderTop: '2px solid #111827', fontWeight: 700 } }}>
                                    <TableCell>Total</TableCell>
                                    <TableCell align="right">{formatCurrency(data.totals?.total_revenue)}</TableCell>
                                    <TableCell align="right">{formatCurrency(data.totals?.total_cost)}</TableCell>
                                    <TableCell align="right">{formatCurrency(data.totals?.total_margin)}</TableCell>
                                    <TableCell align="right">—</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
